import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reportsAPI, permissionTagsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission, SETTINGS_TABS } from '../../contexts/PermissionContext';
import toast from 'react-hot-toast';
import UploadReportModal from './UploadReportModal';
import ReportDataViewer from './ReportDataViewer';
import ReportTagSetupModal from './ReportTagSetupModal';
import AccessTagList from '../../components/AccessTagList';

/**
 * 年度供應商資料頁面（原「雲端報表庫」）
 * 用於管理年度供應商報表（raw data）
 */
export default function Reports() {
  const { userProfile } = useAuth();
  const { canAccessSettingsTab, hasWildcardAccess } = usePermission();
  const queryClient = useQueryClient();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [yearFilter, setYearFilter] = useState('');
  const [tagSetupReport, setTagSetupReport] = useState(null); // 需要設定標籤的報表
  const [tagEditReport, setTagEditReport] = useState(null); // 編輯權限標籤的報表
  const [reportPage, setReportPage] = useState(1);
  const reportsPerPage = 20;

  // 檢查是否為管理員
  const isAdmin = hasWildcardAccess();

  // 只有有權限標籤管理權限的使用者才能上傳報表
  const canUploadReport = canAccessSettingsTab(SETTINGS_TABS.PERMISSION_TAG_MANAGEMENT);

  // 取得報表列表
  const { data: reportsData, isLoading, error } = useQuery({
    queryKey: ['reports'],
    queryFn: reportsAPI.getAll,
  });

  // 刪除報表
  const deleteMutation = useMutation({
    mutationFn: reportsAPI.delete,
    onSuccess: () => {
      toast.success('報表已刪除');
      queryClient.invalidateQueries(['reports']);
      if (selectedReport) {
        setSelectedReport(null);
      }
    },
    onError: (error) => {
      toast.error(error.message || '刪除失敗');
    },
  });

  // 取得唯一年度列表
  const years = useMemo(() => {
    if (!reportsData?.reports) return [];
    const yearSet = new Set(reportsData.reports.map(r => r.year));
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [reportsData]);

  // 篩選報表
  const filteredReports = useMemo(() => {
    if (!reportsData?.reports) return [];
    if (!yearFilter) return reportsData.reports;
    return reportsData.reports.filter(r => r.year === parseInt(yearFilter));
  }, [reportsData, yearFilter]);

  // 報表分頁
  const totalReportPages = Math.ceil(filteredReports.length / reportsPerPage);
  const paginatedReports = filteredReports.slice(
    (reportPage - 1) * reportsPerPage,
    reportPage * reportsPerPage
  );

  // 處理刪除
  const handleDelete = (report) => {
    if (window.confirm(`確定要刪除報表「${report.name}」嗎？此操作無法復原。`)) {
      deleteMutation.mutate(report.id);
    }
  };

  // 格式化日期
  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          載入失敗: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* 頁面標題 */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">年度供應商資料</h1>
          <p className="text-gray-600 mt-1">管理年度供應商報表資料</p>
        </div>
        {canUploadReport && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            上傳報表
          </button>
        )}
      </div>

      {/* 主要內容區 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左側：報表列表 */}
        <div className="lg:col-span-1">
          {/* 大螢幕時最高到視窗可用高度，列表在框內捲動 */}
          <div className="bg-white rounded-lg shadow lg:flex lg:flex-col lg:max-h-[calc(100vh-14rem)]">
            {/* 篩選器 */}
            <div className="p-4 border-b flex-shrink-0">
              <select
                value={yearFilter}
                onChange={(e) => { setYearFilter(e.target.value); setReportPage(1); }}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">所有年度</option>
                {years.map(year => (
                  <option key={year} value={year}>{year} 年</option>
                ))}
              </select>
            </div>

            {/* 報表列表 */}
            <div className="divide-y max-h-[calc(100vh-300px)] overflow-y-auto lg:max-h-none lg:flex-1 lg:min-h-0">
              {isLoading ? (
                <div className="p-4 text-center text-gray-500">載入中...</div>
              ) : filteredReports.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {yearFilter ? '此年度無報表' : '尚無報表'}
                </div>
              ) : (
                paginatedReports.map((report) => (
                  <div
                    key={report.id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition ${
                      selectedReport?.id === report.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    } ${report.needs_tag_setup ? 'bg-yellow-50' : ''}`}
                    onClick={() => {
                      if (report.needs_tag_setup) {
                        // 需要先設定標籤
                        if (canUploadReport) {
                          setTagSetupReport(report);
                        } else {
                          toast.error('此報表尚未設定權限標籤，請聯繫管理員');
                        }
                      } else {
                        setSelectedReport(report);
                      }
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">{report.name}</h3>
                        <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                          <span className="bg-gray-100 px-2 py-0.5 rounded">{report.year} 年</span>
                          <span>{report.row_count?.toLocaleString()} 筆</span>
                        </div>
                        {/* 權限標籤顯示 */}
                        <div className="mt-1 flex items-center gap-1 flex-wrap">
                          {report.needs_tag_setup ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              需設定權限標籤
                            </span>
                          ) : (
                            <AccessTagList tags={report.access_tags || []} resourceLabel="此資料" />
                          )}
                        </div>
                        <div className="mt-1 text-xs text-gray-400">
                          {formatDate(report.uploaded_at)}
                        </div>
                      </div>
                      {canUploadReport && (
                        <div className="flex items-center gap-1">
                          {report.needs_tag_setup ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setTagSetupReport(report);
                              }}
                              className="p-1 text-yellow-600 hover:text-yellow-700 transition"
                              title="設定權限標籤"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                              </svg>
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setTagEditReport(report);
                              }}
                              className="p-1 text-blue-500 hover:text-blue-600 transition"
                              title="編輯權限標籤"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(report);
                            }}
                            className="p-1 text-gray-400 hover:text-red-500 transition"
                            title="刪除"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            {/* 報表列表分頁 */}
            {totalReportPages > 1 && (
              <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                <span className="text-xs text-gray-500">
                  {filteredReports.length} 筆
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setReportPage(p => Math.max(1, p - 1))}
                    disabled={reportPage === 1}
                    className="px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                  >
                    上一頁
                  </button>
                  <span className="px-2 py-1 text-xs text-gray-600">{reportPage}/{totalReportPages}</span>
                  <button
                    onClick={() => setReportPage(p => Math.min(totalReportPages, p + 1))}
                    disabled={reportPage === totalReportPages}
                    className="px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                  >
                    下一頁
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 右側：報表資料檢視器 */}
        <div className="lg:col-span-2">
          {selectedReport ? (
            <ReportDataViewer report={selectedReport} />
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>請從左側選擇報表查看資料</p>
            </div>
          )}
        </div>
      </div>

      {/* 上傳報表 Modal */}
      {showUploadModal && (
        <UploadReportModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            queryClient.invalidateQueries(['reports']);
          }}
        />
      )}

      {/* 設定權限標籤 Modal */}
      {tagSetupReport && (
        <ReportTagSetupModal
          report={tagSetupReport}
          isEditMode={false}
          onClose={() => setTagSetupReport(null)}
          onSuccess={() => {
            setTagSetupReport(null);
            queryClient.invalidateQueries(['reports']);
          }}
        />
      )}

      {/* 編輯權限標籤 Modal */}
      {tagEditReport && (
        <ReportTagSetupModal
          report={tagEditReport}
          isEditMode={true}
          onClose={() => setTagEditReport(null)}
          onSuccess={() => {
            setTagEditReport(null);
            queryClient.invalidateQueries(['reports']);
          }}
        />
      )}
    </div>
  );
}
